"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Database, Cloud, Monitor, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

interface CachePurgeModalProps {
  open: boolean;
  onClose: () => void;
}

const CACHE_ITEMS = [
  {
    key: "redis" as const,
    icon: Database,
    label: "Redis",
    description: "Sunucu tarafı uygulama cache'i (ayarlar, sık kullanılan veriler)",
  },
  {
    key: "cloudflare" as const,
    icon: Cloud,
    label: "Cloudflare CDN",
    description: "Edge sunucularındaki cache (sayfalar, API yanıtları)",
  },
  {
    key: "web" as const,
    icon: Monitor,
    label: "Web",
    description: "Admin panel sorgu cache'i (anlık verileri yeniler)",
  },
];

export function CachePurgeModal({ open, onClose }: CachePurgeModalProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState({ redis: true, cloudflare: true, web: true });
  const [loading, setLoading] = useState(false);

  const toggle = (key: keyof typeof selected) =>
    setSelected((s) => ({ ...s, [key]: !s[key] }));

  const noneSelected = !selected.redis && !selected.cloudflare && !selected.web;

  const handlePurge = async () => {
    setLoading(true);
    try {
      if (selected.redis || selected.cloudflare) {
        await api.post("/api/admin/cache/purge", {
          redis: selected.redis,
          cloudflare: selected.cloudflare,
        });
      }

      if (selected.web) {
        queryClient.clear();
      }

      const labels = CACHE_ITEMS.filter((i) => selected[i.key]).map((i) => i.label);
      toast.success(`${labels.join(", ")} cache temizlendi.`);
      onClose();
    } catch {
      toast.error("Cache temizlenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Cache Temizle" width="max-w-sm">
      <div className="p-5 space-y-2">
        {CACHE_ITEMS.map(({ key, icon: Icon, label, description }) => (
          <div
            key={key}
            onClick={() => toggle(key)}
            className={`flex items-center gap-4 rounded-xl border px-4 py-3.5 cursor-pointer transition-all select-none ${
              selected[key]
                ? "border-primary/30 bg-primary/[0.03] dark:border-primary/20 dark:bg-primary/[0.05]"
                : "border-slate-200/80 bg-white hover:bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/40"
            }`}
          >
            <div
              className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                selected[key]
                  ? "bg-primary/10 text-primary"
                  : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
              }`}
            >
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold leading-none mb-1 ${selected[key] ? "text-primary" : "text-slate-700 dark:text-slate-200"}`}>
                {label}
              </p>
              <p className="text-xs text-muted-foreground leading-snug">{description}</p>
            </div>
            <Switch
              checked={selected[key]}
              onCheckedChange={() => toggle(key)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ))}
      </div>

      <div className="px-5 pb-5">
        <Button
          className="w-full"
          onClick={handlePurge}
          disabled={loading || noneSelected}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Temizleniyor…" : "Temizle"}
        </Button>
      </div>
    </Modal>
  );
}
